import { useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToken } from "@packsize/authentication";

import { getActiveBatches } from "@/api/selection-api";
import { useCancellingQuery } from "@/hooks/use-cancelling-query";
import { useSignalRHub } from "@/hooks/use-signalr-hub";
import { baseRoutes } from "@/api/base-routes";

interface UseActiveBatchesParams {
	machineGroupId: string | undefined;
	productionGroupId: string | undefined;
	scanToTriggerSingleConfigured: boolean;
}

export const useActiveBatches = ({
	machineGroupId,
	productionGroupId,
	scanToTriggerSingleConfigured,
}: UseActiveBatchesParams) => {
	const targetId = machineGroupId || productionGroupId;
	const isUsingMachineGroup = Boolean(machineGroupId);

	const queryKey = useMemo(
		() =>
			[
				isUsingMachineGroup ? "machineGroupBatchQueue" : "productionGroupBatchQueue",
				{ machineGroupId, productionGroupId, scanToTriggerSingleConfigured },
			] as const,
		[isUsingMachineGroup, machineGroupId, productionGroupId, scanToTriggerSingleConfigured],
	);

	const queryClient = useQueryClient();
	const { bearerToken } = useToken();

	const { hubConnection, usePolling, hubConnectionEstablished } = useSignalRHub({
		hubUrl: `${baseRoutes.selectionHub}/batches`,
		token: bearerToken,
	});

	const queryResults = useCancellingQuery({
		queryKey,
		queryFn: () =>
			getActiveBatches({
				machineGroupId: machineGroupId!,
				scanToTriggerSingleConfigured,
			}),
		refetchInterval: usePolling ? 3000 : 60000,
		enabled: Boolean(targetId),
	});

	useEffect(() => {
		if (queryResults.data?.some((batch) => batch.status === "Completed")) {
			setTimeout(() => {
				queryClient.invalidateQueries({ queryKey });
			}, 10000);
		}
	}, [queryClient, queryKey, queryResults.data]);

	useEffect(() => {
		if (!hubConnection || !hubConnectionEstablished || !targetId) return;

		const entityType = isUsingMachineGroup ? "machine group" : "production group";

		const handleActiveBatchesUpdated = (updatedId: string) => {
			if (updatedId !== targetId) return;
			queryClient.invalidateQueries({ queryKey });
		};

		hubConnection.on("ProductionGroupBatchesUpdated", handleActiveBatchesUpdated);
		hubConnection.on("MachineGroupBatchesUpdated", handleActiveBatchesUpdated);

		const subscribeMethod = isUsingMachineGroup ? "SubscribeToMachineGroup" : "SubscribeToProductionGroup";
		const unsubscribeMethod = isUsingMachineGroup ? "UnsubscribeFromMachineGroup" : "UnsubscribeFromProductionGroup";

		hubConnection.invoke(subscribeMethod, targetId).catch((error) => {
			console.error(`Failed to subscribe to ${entityType}:`, error);
		});

		return () => {
			hubConnection.off("ProductionGroupBatchesUpdated", handleActiveBatchesUpdated);
			hubConnection.off("MachineGroupBatchesUpdated", handleActiveBatchesUpdated);
			hubConnection.invoke(unsubscribeMethod, targetId).catch((error) => {
				console.error(`Failed to unsubscribe from ${entityType}:`, error);
			});
		};
	}, [hubConnection, hubConnectionEstablished, targetId, isUsingMachineGroup, queryClient, queryKey]);

	return {
		queryKey,
		...queryResults,
	};
};
